import React from 'react';
import { defineMessages, FormattedTime, FormattedMessage, MessageDescriptor } from 'react-intl';
import { IServiceDetails, IService, calculateGrossPrice } from 'src/entities/service';
import { ISchedule } from 'src/entities/schedule';
import { useState, useRef } from 'react';
import globalMessages from 'src/services/globalMessages';
import Popup from 'src/components/Popup/Popup';
import ServiceDescription from 'src/components/ServiceDescription/ServiceDescription';
import PrimaryButton from 'src/components/form-components/PrimaryButton/PrimaryButton';
import { useOutsideClick } from 'src/hooks';
import classNames from 'classnames';
import PersonCount from '../DateTimeSelect/PersonCount/PersonCount';
import { getPaymentNeed } from '../../ReservationEnd/functions/helperFunctions';
import FormattedPrice from '../ServiceSelect/FormattedPrice/FormattedPrice';

const messages = defineMessages({
    seat: {
        id: 'seat',
        defaultMessage: '{n} hely'
    }
});

export const isFullSchedule = (freeSeatsCount: number): boolean => {
    return freeSeatsCount === 0;
};

export const isPassedSchedule = (scheduleStart: string, minTimeReserveHours: number, firstFreeSlot?: string): boolean => {
    const scheduleStartDateTime = new Date(scheduleStart);
    
    if (firstFreeSlot && minTimeReserveHours > 0) {
        const firstFreeSlotDateTime = new Date(firstFreeSlot);
        return scheduleStartDateTime < firstFreeSlotDateTime;
    }

    const minTimeReserveDateTime = new Date();
    minTimeReserveDateTime.setHours(minTimeReserveDateTime.getHours() + minTimeReserveHours);
    
    return scheduleStartDateTime < minTimeReserveDateTime;
};

export const isWeekend = (day: number): boolean => {
    return day === 6 || day === 0;
};

const Summary: React.FC<{
    service: IServiceDetails;
    schedule: ISchedule;
    minTimeReserve: number;
    hideSeatCount: boolean;
    isMobile?: boolean;
    firstFreeSlot?: string; 
}> = ({ service, schedule, minTimeReserve, hideSeatCount, isMobile, firstFreeSlot }) => {
    const { IsDurationVisible, DurationToUser, DisplayedPrice, DisplayedCurrency } = service;
    const { FreeSeatsCount, Start } = schedule;

    return (
        <section className="event__summary">
            <h3 className="event__name">{service.Name}</h3>
            <div className="event__times">
                <div className="event__times-inner">
                    <time>
                        <FormattedTime value={Start} />
                    </time>
                    {IsDurationVisible && (
                        <>
                            {' | '}
                            <time dateTime={`P${DurationToUser}M`}>
                                {DurationToUser}
                                {'′'}
                            </time>
                        </>
                    )}
                    {DisplayedPrice && isMobile ? (
                        <>
                            {' | '}{' '}
                            <FormattedPrice value={DisplayedPrice} currency={DisplayedCurrency} />
                        </>
                    ) : (
                        <></>
                    )}
                </div>
            </div>
            <div className="event__seats">
                {isFullSchedule(FreeSeatsCount) ? (
                    <FormattedMessage {...globalMessages.full} />
                ) : isPassedSchedule(schedule.Start, minTimeReserve, firstFreeSlot) ? (
                    <FormattedMessage {...globalMessages.notAvailable} />
                ) : hideSeatCount ? null : (
                    <FormattedMessage {...messages.seat} values={{ n: FreeSeatsCount }} />
                )}
            </div>
        </section>
    );
};

const useDetailsElement = (detailsRef: React.RefObject<HTMLDetailsElement>) => {
    const [open, setOpen] = useState(false);

    useOutsideClick(detailsRef, () => {
        setOpen(false);
    });

    const handleClickOnDetails = (e: React.MouseEvent<HTMLDetailsElement>) => {
        const { target } = e;
        const shouldStay =
            target instanceof Element && (target.closest('select') || target.closest('.link'));
        setOpen((prev) => (shouldStay ? prev : !prev));
    };

    const handleClickOnSummary = (e: React.MouseEvent<HTMLElement>) => e.preventDefault();

    return {
        open,
        handleClickOnDetails,
        handleClickOnSummary,
    };
};

const useAvailability = (
    isReadonly: boolean,
    freeSeatsCount: number,
    start: string,
    minTimeReserveHours: number,
    firstFreeSlot?: string
) => {
    const isFull = isFullSchedule(freeSeatsCount);
    const isPassed = isPassedSchedule(start, minTimeReserveHours, firstFreeSlot);
    const isAvailable = !isReadonly && !isFull && !isPassed;
    const notAvailableText: MessageDescriptor | undefined = isPassed
        ? globalMessages.notAvailable
        : isFull
        ? globalMessages.full
        : undefined;

    return {
        isAvailable,
        notAvailableText,
    };
};

export const EventMobile: React.FC<IEventProps & { isListView: boolean }> = ({
    service,
    schedule,
    handleServiceSelect,
    minTimeReserve,
    isListView,
    isReadonly,
    hideSeatCount,
    isPaymentEnabled,
    firstFreeSlot
}) => {
    const { Description, ShortDescription, Price, Currency, PaymentMethod, VatPercentage } =
        service;
    const { FreeSeatsCount, ReservationLimit } = schedule;

    const needsPayment = getPaymentNeed(isPaymentEnabled, service.Payable);

    const [isLongDescriptionVisible, setIsLongDescriptionVisible] = useState(false);
    const [personCount, setPersonCount] = useState(1);

    const detailsRef = useRef<HTMLDetailsElement>(null);

    const { open, handleClickOnSummary, handleClickOnDetails } = useDetailsElement(detailsRef);

    const { isAvailable, notAvailableText } = useAvailability(
        isReadonly,
        FreeSeatsCount,
        schedule.Start,
        minTimeReserve,
        firstFreeSlot
    );

    const event = (
        <details
            className={classNames('event', 'timetable__event--mobile', { 'event--open': open })}
            ref={detailsRef}
            style={{ color: service.Background }}
            open={open}
            onClick={handleClickOnDetails}
        >
            <summary onClick={handleClickOnSummary}>
                <Summary
                    service={service}
                    schedule={schedule}
                    minTimeReserve={minTimeReserve}
                    hideSeatCount={hideSeatCount}
                    isMobile={true}
                    firstFreeSlot={firstFreeSlot}
                />
            </summary>

            <div className="event__description">
                {needsPayment && (
                    <div className="event__price">
                        <FormattedMessage {...globalMessages.toBePaid} />
                        <FormattedPrice
                            value={calculateGrossPrice({
                                Price,
                                Currency,
                                PaymentMethod,
                                VatPercentage,
                            })}
                            currency={Currency}
                        />
                    </div>
                )}
                {ShortDescription && <p>{ShortDescription}</p>}
                {schedule.Note && <p>{schedule.Note}</p>}
                {Description && (
                    <>
                        <button onClick={() => setIsLongDescriptionVisible(true)} className="link">
                            <FormattedMessage {...globalMessages.readMore} />
                        </button>
                        <Popup
                            visible={isLongDescriptionVisible}
                            closePopup={() => setIsLongDescriptionVisible(false)}
                            title={service.Name}
                        >
                            <ServiceDescription
                                schedule={schedule}
                                service={service}
                                notAvailableText={notAvailableText}
                                needsPayment={needsPayment}
                            />
                        </Popup>
                    </>
                )}
            </div>
            {isAvailable && (
                <div className="event__actions">
                    <PersonCount
                        maxCount={ReservationLimit}
                        count={personCount}
                        onChange={setPersonCount}
                    />
                    <PrimaryButton
                        className="event__button"
                        caption={globalMessages.serviceDescriptionStartReservation}
                        onClick={() => handleServiceSelect(service, schedule, personCount)}
                    />
                </div>
            )}
        </details>
    );

    return isListView ? <div className="timetable__event--list">{event}</div> : event;
};

interface IEventProps {
    service: IServiceDetails,
    schedule: ISchedule,
    handleServiceSelect: (service: IService, schedule: ISchedule, personCount: number) => void,
    minTimeReserve: number,
    isReadonly: boolean,
    hideSeatCount: boolean,
    isPaymentEnabled?: boolean,
    firstFreeSlot?: string
}

export const Event: React.FC<IEventProps> = ({ service, schedule, handleServiceSelect, minTimeReserve, isReadonly, hideSeatCount, isPaymentEnabled, firstFreeSlot }) => {
    const [isLongDescriptionVisible, setIsLongDescriptionVisible] = useState(false);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target instanceof Element) {
            setIsLongDescriptionVisible(true)
        }
    }

    const { isAvailable, notAvailableText } = useAvailability(isReadonly, schedule.FreeSeatsCount, schedule.Start, minTimeReserve, firstFreeSlot);

    return (
        <>
            <div
                onClick={handleClick}
                className="event timetable__event"
                style={{ color: service.Background }}
                tabIndex={0}
            >
                <Summary 
                    service={service}
                    schedule={schedule}
                    minTimeReserve={minTimeReserve}
                    hideSeatCount={hideSeatCount}
                    firstFreeSlot={firstFreeSlot} />
            </div>

            <Popup
                visible={isLongDescriptionVisible}
                closePopup={() => setIsLongDescriptionVisible(false)}
                title={service.Name}>
                <ServiceDescription
                    schedule={schedule}
                    service={service}
                    startReservation={isAvailable
                        ? (personCount) => handleServiceSelect(service, schedule, personCount!)
                        : undefined}
                    notAvailableText={notAvailableText}
                    needsPayment={getPaymentNeed(isPaymentEnabled, service.Payable)}
                />
            </Popup>
        </>
    )
}
